import compact from 'lodash/compact'
import toPath from 'lodash/toPath'
import PropTypes from 'prop-types'
import { isEmpty } from 'ramda'
import React from 'react'
import { connect } from 'react-redux'
import { Element, scroller } from 'react-scroll'
import { compose } from 'recompose'
import { softUpdateApplicationInformation, updateApplicationInformation } from '../../actions/FlowAction'
import { isBrusselsZipCode } from '../../services/routeService'
import * as translationService from '../../services/translationService'
import { getRandomString } from '../../utils/generator'
import { validateContract } from '../../utils/validation'
import {
  AddressField,
  Button,
  Caption,
  DisplayText,
  ErrorText,
  Icon,
  MobileNumber,
  Modal,
  ModalBody,
  Notice,
  Option,
  PlaceAutocomplete,
  Stack,
  TextContainer,
  TextField,
  UploadField,
} from './../../components'
import SceneComponent from './SceneComponent'

const defaultPerson = {
  uid: getRandomString(),
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  documents: [],
  income: [],
  hasReleventIncome: false,
  address: {
    number: '',
    street: '',
    city: '',
    zip: '',
    countryId: '',
  },
}

function DocumentUploadField({
  title,
  description = null,
  extraPopup = null,
  limit = 2,
  person,
  errors,
  handleDocumentsChange,
  handleHandOffChange,
  handleToggleChange,
  index,
}) {
  return (
    <TextContainer>
      <DisplayText size="small">{title}</DisplayText>
      {description && (
        <Caption>
          {description}{' '}
          {extraPopup && (
            <Button onClick={() => handleToggleChange()} plain>
              {extraPopup}
            </Button>
          )}
        </Caption>
      )}
      <UploadField
        id={`files-${index}`}
        value={compact(person.documents)}
        limit={limit}
        disabled={person.handOff}
        onChange={documents => handleDocumentsChange(documents)}
        error={errors && errors.documents}
      />
      <Option
        type="checkbox"
        label={translationService.documentsHandOff()}
        id={`handOff-${index}`}
        name="handOff"
        checked={person.handOff}
        onChange={event => handleHandOffChange(event)}
      />
      {errors && errors.handOff && <ErrorText transKey={errors.handOff} />}
    </TextContainer>
  )
}

DocumentUploadField.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  person: PropTypes.object,
  errors: PropTypes.any,
  handleDocumentsChange: PropTypes.func,
  handleHandOffChange: PropTypes.func,
  index: PropTypes.number,
  limit: PropTypes.number,
  extraPopup: PropTypes.string,
  handleToggleChange: PropTypes.func,
}

class ContractScene extends SceneComponent {
  static propTypes = {
    application: PropTypes.any,
    property: PropTypes.object,
  }

  state = {
    contacts: [],
    infoModalIsOpen: false,
    isBrusselsZip: isBrusselsZipCode(this.props.property.zip),
  }

  componentDidMount(...args) {
    const { contacts, acceptDataUsages } = this.props.application

    super.componentDidMount.apply(this, args)
    const { zip } = this.props.property
    this.setState({
      isBrusselsZip: isBrusselsZipCode(zip),
    })
    const isBrusselsZip = isBrusselsZipCode(zip)
    if (this.props.checkForError) {
      return validateContract({ contacts, acceptDataUsages }, false, isBrusselsZip).catch(fieldErrors => {
        console.warn('ContractScene, componentDidMount', { fieldErrors })
        this.setState(
          {
            fieldErrors,
          },
          () => {
            scroller.scrollTo('mainError', {
              smooth: true,
              offset: -40, // Scrolls to element - 40 pixels down the page
              containerId: this.props.inModal && 'sceneModal',
            })
          },
        )
      })
    }
  }

  addPerson = event => {
    const { contacts } = this.props.application
    event.preventDefault()
    contacts.push(Object.assign({}, defaultPerson))
    this.props.softUpdateApplicationInformation({ contacts })
  }

  removePerson = (event, index) => {
    const { contacts } = this.props.application
    event.preventDefault()
    contacts.splice(index, 1)
    this.props.softUpdateApplicationInformation({ contacts })
  }

  onSubmit = (shouldValidate = true) => {
    const { contacts, acceptDataUsages } = this.props.application
    this.props.softUpdateApplicationInformation({ contacts, acceptDataUsages })

    if (shouldValidate) {
      return validateContract({ contacts, acceptDataUsages }, false, this.state.isBrusselsZip)
        .then(() => {
          return this.props.updateApplicationInformation({ contacts, acceptDataUsages })
        })
        .catch(fieldErrors => {
          this.setState(
            {
              fieldErrors,
            },
            () => {
              scroller.scrollTo('mainError', {
                smooth: true,
                offset: -40, // Scrolls to element - 40 pixels down the page
                containerId: this.props.inModal && 'sceneModal',
              })
            },
          )
          throw fieldErrors
        })
    } else {
      return this.props.updateApplicationInformation({ contacts, acceptDataUsages })
    }
  }

  resetErrors = () => {
    this.setState({
      fieldErrors: {},
    })
  }

  onChange = ({ target: { name, value } }, personIndex) => {
    const { contacts } = this.props.application
    contacts[personIndex][name] = value
    this.props.softUpdateApplicationInformation({ contacts })
  }
  toggleModal = () => {
    this.setState(prevState => {
      return { infoModalIsOpen: !prevState.infoModalIsOpen }
    })
  }

  onCheckboxChange = ({ target: { checked, name } }, personIndex) => {
    this.onChange({ target: { name, value: checked } }, personIndex)
  }

  onHandOffChange = ({ target: { checked, name } }, personIndex) => {
    const { contacts } = this.props.application
    contacts[personIndex][name] = checked
    this.props.softUpdateApplicationInformation({ contacts })
  }

  onDocumentsChange = (documents, personIndex) => {
    // Errors are calculated on mount, so the "document required" error would stay visible
    // under the field after a successful upload and read as a failed upload.
    this.resetErrors()
    const { contacts } = this.props.application
    contacts[personIndex].documents = documents
    this.props.softUpdateApplicationInformation({ contacts })
  }

  handleGoogleAddressSelect = (address, personIndex) => {
    const { contacts } = this.props.application
    contacts[personIndex].address = address
    this.props.softUpdateApplicationInformation({ contacts })
  }

  handleAddressChange = ({ target: { value, name } }, personIndex) => {
    const { contacts } = this.props.application
    const addressItemKey = toPath(name)[1]
    const newContacts = contacts.map((contact, index) => {
      return personIndex === index
        ? {
            ...contact,
            address: {
              ...contact.address,
              [addressItemKey]: value,
            },
          }
        : contact
    })
    this.props.softUpdateApplicationInformation({ contacts: newContacts })
  }

  handleAcceptDataUsages = ({ target: { checked } }) => {
    this.props.softUpdateApplicationInformation({ acceptDataUsages: checked })
  }

  render() {
    const { fieldErrors = [] } = this.state
    const { contacts } = this.props.application
    const { zip } = this.props.property

    return (
      <div>
        <TextContainer>
          <DisplayText>{translationService.contractTitle()}</DisplayText>
          {!isEmpty(fieldErrors) && (
            <Element name="mainError">
              <Notice type="error">{translationService.intakeFormErrors()}</Notice>
            </Element>
          )}
          <div>
            {contacts.map((person, index) => {
              const documentProps = {
                title: `${translationService.t('intake.contract.id.title')}`,
                description: `${translationService.t('intake.contract.id.description')}`,
                extraPopup: `${translationService.t('intake.contract.id.more.info.action')}`,
                handleToggleChange: () => this.toggleModal(),
                person,
                index,
                handleDocumentsChange: documents => this.onDocumentsChange(documents, index),
                handleHandOffChange: event => this.onHandOffChange(event, index),
              }

              if (fieldErrors.contacts && fieldErrors.contacts[index] && fieldErrors.contacts[index].documents) {
                documentProps.errors = fieldErrors.contacts[index].documents
              }

              return (
                <TextContainer key={index} asCard={true}>
                  {index === 0 ? (
                    <DisplayText size="small">
                      {person.firstname} {person.lastname}
                    </DisplayText>
                  ) : (
                    <React.Fragment>
                      <Stack distribution="equalSpacing">
                        <DisplayText size="small">
                          {person.firstname || person.lastname
                            ? `${person.firstname} ${person.lastname}`
                            : `${translationService.t('system.tenant')} ${index + 1}`}
                        </DisplayText>
                        <Button plain onClick={event => this.removePerson(event, index)}>
                          <Icon source="bin" />
                        </Button>
                      </Stack>
                      <Option
                        type="checkbox"
                        label={translationService.t('intake.person.is.guarantor')}
                        id={`isGuarantor-${index}`}
                        name="isGuarantor"
                        checked={person.isGuarantor}
                        onChange={event => this.onCheckboxChange(event, index)}
                      />
                      <div>
                        <Stack distribution="fill">
                          <TextField
                            error={
                              fieldErrors.contacts &&
                              fieldErrors.contacts[index] &&
                              fieldErrors.contacts[index].firstname
                            }
                            label={`${translationService.t('system.firstname')}`}
                            id={`firstname-${index}`}
                            name="firstname"
                            value={person.firstname}
                            onChange={event => this.onChange(event, index)}
                          />
                          <TextField
                            error={
                              fieldErrors.contacts &&
                              fieldErrors.contacts[index] &&
                              fieldErrors.contacts[index].lastname
                            }
                            label={`${translationService.t('system.lastname')}`}
                            id={`lastname-${index}`}
                            name="lastname"
                            value={person.lastname}
                            onChange={event => this.onChange(event, index)}
                          />
                        </Stack>
                      </div>
                      <TextField
                        error={fieldErrors.contacts && fieldErrors.contacts[index] && fieldErrors.contacts[index].email}
                        label={`${translationService.t('system.email')}`}
                        id={`email-${index}`}
                        name="email"
                        value={person.email}
                        onChange={event => this.onChange(event, index)}
                      />
                      <MobileNumber
                        error={fieldErrors.contacts && fieldErrors.contacts[index] && fieldErrors.contacts[index].phone}
                        label={`${translationService.t('system.mobile_number')}`}
                        id={`phone-${index}`}
                        name="phone"
                        value={person.phone}
                        onChange={event => this.onChange(event, index)}
                      />
                      <PlaceAutocomplete onSelect={address => this.handleGoogleAddressSelect(address, index)} />
                      <AddressField
                        type="text"
                        name={`address-${index}`}
                        id={`address-${index}`}
                        address={{ ...person.address }}
                        onChange={event => this.handleAddressChange(event, index)}
                      />
                    </React.Fragment>
                  )}
                  {!isBrusselsZipCode(zip) && (
                    <DocumentUploadField
                      key={index}
                      {...documentProps}
                      errors={fieldErrors.contacts && fieldErrors.contacts[index]}
                    />
                  )}
                </TextContainer>
              )
            })}
          </div>
          <p>{translationService.t('intake.add.tenant.intro')}</p>
          <div>
            <Button outline onClick={this.addPerson}>
              + {translationService.t('intake.add.signatory')}
            </Button>
          </div>
          <div>
            <Option
              type="checkbox"
              label={translationService.applicationAcceptDataUsage()}
              id="acceptDataUsages"
              name="acceptDataUsages"
              checked={this.props.application.acceptDataUsages || false}
              onChange={this.handleAcceptDataUsages}
            />
            {fieldErrors.acceptDataUsages && <ErrorText transKey={fieldErrors.acceptDataUsages} />}
          </div>
        </TextContainer>
        {/*<pre>{JSON.stringify(contacts, null, 4)}</pre>*/}
        <Modal
          isOpen={this.state.infoModalIsOpen}
          onRequestClose={this.toggleModal}
          title={translationService.contractMoreInfoTitle()}>
          <ModalBody>{translationService.contractMoreInfoText()}</ModalBody>
        </Modal>
      </div>
    )
  }
}

const mapStateToProps = ({ FlowReducer }) => {
  const { application, property } = FlowReducer
  return {
    application,
    property,
  }
}
const mapDispatchToProps = dispatch => ({
  updateApplicationInformation: information => dispatch(updateApplicationInformation(information)),
  softUpdateApplicationInformation: information => dispatch(softUpdateApplicationInformation(information)),
})

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(ContractScene)



// WEBPACK FOOTER //
// ./src/views/scenes/ContractScene.js